import { createStandaloneToast } from '@chakra-ui/react';
import axios from 'axios';
import { TEMPORARY } from 'helpers/constant';
import { CONVERSATION_STATUS } from 'helpers/enum';
import { RootState } from 'helpers/store';
import { ConversationService } from 'services/conversation/conversation.service';
import { GetDetailConversationResponse } from 'services/conversation/conversation.type';
import { call, put, select, takeEvery } from 'typed-redux-saga';
import {
  AddNewQuestionAction,
  DeleteConversationAction,
  EditConversationNameAction,
  EditQuestionAction,
  GetConversationsAction,
  GetDetailConversationAction,
} from 'types/actions/conversation';

import { conversationAction } from '../actions';
import { conversationConstant } from '../actionTypes';

const { toast } = createStandaloneToast();

const getConversationsData = function* ({ payload }: GetConversationsAction) {
  try {
    const result = yield* call(() => {
      return ConversationService.getConversations(payload);
    });
    const state = yield* select((state: RootState) => state.conversations);
    const conversations = state?.conversations;
    const newConversations = { ...result };
    const temporaryConversations = conversations.data.filter(
      (conversation) => conversation.type === TEMPORARY
    );

    if (temporaryConversations && payload?.isKeepTemporaryConversation) {
      newConversations.data = [...temporaryConversations, ...newConversations.data];
    }

    if (payload?.page) {
      newConversations.data = [...conversations.data, ...result.data];
    }

    yield* put(
      conversationAction.updateConversations({
        ...newConversations,
        current_page: payload?.page ?? 0,
      })
    );
  } catch (error) {
    console.log(error);
  }
};

const getDetailConversationData = function* ({ payload }: GetDetailConversationAction) {
  let result: GetDetailConversationResponse = [];
  if (payload?.type !== TEMPORARY) {
    result = yield* call(() => {
      return ConversationService.getDetailConversation({
        conversation_id: payload.conversation_id,
      });
    });
  }
  const state = yield* select((state: RootState) => state.conversations);

  if (payload.conversation_id === state.current.conversation_id) {
    yield* put(conversationAction.updateCurrentConversationData(result));
  }
};

const editConversationName = function* ({ payload }: EditConversationNameAction) {
  const state = yield* select((state: RootState) => state.conversations);
  const editConversationState = state.conversations.data.find(
    (conversation) => conversation.id === payload.id
  );

  const isTemporaryConversation = editConversationState?.type === TEMPORARY;
  const isCurrentConversation = payload.id === state.current.conversation_id;

  // Not allow to edit name temporary conversation
  if (isTemporaryConversation) {
    return;
  }

  const result = yield* call(() => {
    return ConversationService.editConversationName(payload);
  });
  if (isCurrentConversation) {
    const newConversations = state.conversations;

    newConversations.data = newConversations.data.filter(
      (conversation) => conversation.id !== payload.id
    );
    yield* put(conversationAction.updateConversations(newConversations));
  }
  toast({
    title: `Renamed to ${result.name}`,
    status: 'success',
    position: 'bottom-right',
    isClosable: true,
  });
  yield* put(conversationAction.getConversations({ isKeepTemporaryConversation: true }));
};

const deleteConversation = function* ({ payload }: DeleteConversationAction) {
  const state = yield* select((state: RootState) => state.conversations);
  const deleteConversationState = state.conversations.data.find(
    (conversation) => conversation.id === payload.id
  );

  const isTemporaryConversation = deleteConversationState?.type === TEMPORARY;
  const isCurrentConversation = payload.id === state.current.conversation_id;

  if (!isTemporaryConversation) {
    yield* call(() => {
      return ConversationService.deleteConversation(payload);
    });
  }
  if (isCurrentConversation) {
    const newConversations = state.conversations;

    newConversations.data = newConversations.data.filter(
      (conversation) => conversation.id !== payload.id
    );
    yield* put(conversationAction.updateConversations(newConversations));
    yield* put(conversationAction.updateCurrentConversation({ conversation_id: null, type: '' }));
  }
  yield* put(conversationAction.getConversations({ isKeepTemporaryConversation: true }));
};

const addNewQuestion = function* ({ payload }: AddNewQuestionAction) {
  const state = yield* select((state: RootState) => state.conversations);
  const isCurrentTemporaryConversation = state?.current?.type === TEMPORARY;
  try {
    yield* put(conversationAction.updateNewQuestion({ ...payload, status: 'loading' }));

    const result = yield* call(() => {
      return ConversationService.addQuestion({
        question: payload?.question,
        ...(!isCurrentTemporaryConversation && {
          conversation_id: state?.current?.conversation_id,
        }),
      });
    });
    if (isCurrentTemporaryConversation) {
      yield* put(conversationAction.getConversations());
      yield* put(
        conversationAction.updateCurrentConversation({
          conversation_id: result.id,
        })
      );
      yield* put(conversationAction.updateProcessConversationId(result.id));
    }
  } catch (error) {
    if (state?.current?.conversation_id) {
      yield* put(
        conversationAction.updateNewAnswer({
          status: 'error',
          conversation_id: state.current.conversation_id,
        })
      );
    }

    if (axios.isAxiosError(error) && error.response?.data?.error_message) {
      if (
        error?.response?.data?.error_message ===
        'Exceeded maximum number of question per organization'
      ) {
        yield* put(conversationAction.updateLimitStatus(true));
      }
      toast({
        title: error?.response?.data?.error_message,
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  }
};

const editQuestion = function* ({ payload }: EditQuestionAction) {
  const state = yield* select((state: RootState) => state.conversations);
  try {
    yield* put(conversationAction.updateCurrentQuestion({ ...payload }));
    yield* put(conversationAction.updateStatus(CONVERSATION_STATUS.GENERATE_ANSWER));
    if (state.current.conversation_id) {
      yield* put(conversationAction.updateProcessConversationId(state.current.conversation_id));
    }

    const res = yield* call(() => {
      return ConversationService.editQuestion({
        question: payload?.question,
        conversation_id: state?.current?.conversation_id,
      });
    });
    if (res?.success === false) {
      yield* put(conversationAction.updateStatus(CONVERSATION_STATUS.IDLE));
    }
  } catch (error) {
    if (state?.current?.conversation_id) {
      yield* put(
        conversationAction.updateNewAnswer({
          status: 'error',
          conversation_id: state.current.conversation_id,
        })
      );
    }
    console.log(error);
    yield* put(conversationAction.updateStatus(CONVERSATION_STATUS.IDLE));

    if (axios.isAxiosError(error) && error?.response?.data?.error_message) {
      if (
        error?.response?.data?.error_message ===
        'Exceeded maximum number of question per organization'
      ) {
        yield* put(conversationAction.updateLimitStatus(true));
      }
      toast({
        title: error?.response?.data?.error_message,
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  }
};

export const watchGetConversations = function* () {
  yield* takeEvery(conversationConstant.GET_CONVERSATIONS, getConversationsData);
};

export const watchGetDetailConversation = function* () {
  yield* takeEvery(conversationConstant.GET_DETAIL_CONVERSATION, getDetailConversationData);
};

export const watchEditConversationName = function* () {
  yield* takeEvery(conversationConstant.EDIT_CONVERSATION_NAME, editConversationName);
};

export const watchAskQuestion = function* () {
  yield* takeEvery(conversationConstant.ADD_NEW_QUESTION, addNewQuestion);
};

export const watchEditQuestion = function* () {
  yield* takeEvery(conversationConstant.EDIT_QUESTION, editQuestion);
};

export const watchDeleteConversation = function* () {
  yield* takeEvery(conversationConstant.DELETE_CONVERSATION, deleteConversation);
};
